// @flow

import React, { useState } from 'react';
import * as R from 'ramda';
import { useSelector, useDispatch } from 'react-redux';
import {
  log,
  formatPhoneNumberForBackend,
  formatPhoneNumberForFrontend,
  validateEmail
} from '../../utils/jsUtils';
import modelUtils from '../../utils/modelUtils';
import TenantsDropzone from './TenantsFileDrop';
import moment from 'moment';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';

import EditIcon from '@mui/icons-material/Edit';
import RemoveIcon from '@mui/icons-material/DeleteOutline';
import PhoneIcon from '@mui/icons-material/Phone';

import { calculateCreditsForMsg } from '../../models/Message';
import TagRenderer from '../SharedComponents/TagRenderer';
import ConfigurableTable from '../SharedComponents/ConfigurableTable';
import ContentContainer from '../SharedComponents/ContentContainer';
import MessageTenantDialog from './MessageTenantDialog';
import RemoveTenantDialog from './RemoveTenantDialog';
import EditTenantDialog from './EditTenantDialog';
import VoiceCallDialog from './VoiceCallDialog';
import ConfirmTenant from './ConfirmTenantDialog';
import SPBackend, { NOTIFICATION_CHANNELS } from '../../services/SPBackend';
import Paginator from '../SharedComponents/Paginator';

// Import Redux selectors
import { selectUser, selectAccountSettings, selectDefaultUnit, selectTags, selectAccountUsage } from '../../reduxStore/modules/user/selectors';
import { selectTenantsPageInfo, selectTenantEnableUpload } from '../../reduxStore/modules/tenants/selectors';
import { setSnackMessage, setLoadingModule } from '../../reduxStore/modules/common/actions';
import { setTenants } from '../../reduxStore/modules/tenants/actions';
import { useI18n } from '../../contexts/I18nContext';

import { createClasses } from '../../utils/stylesUtils';
import styles from './Styles/TenantsContent.Style';
import MemberStructuralStyles from '../Styles/CommonMemberStructuralStyles.Style';
import baseTableStyles from '../Styles/BaseTableStyles.Style';

import type { Tenant, Unit, Tag } from '../../services/SPBackend';

type Props = {
  tenants: Array<Tenant>,
  selectedRowsCount: number,
  isCustomerSelected: Function,
  handleSelectAllClick: Function,
  handleSelectCustomer: Function,
  hasInitiallyLoaded?: boolean,
  removeTenant: Function,
  setTenants: Function,
  orderTenants: Function,
  tenantsNextPage: Array<Tenant>,
  totalTenantsCount: number,
  setCountOfTenants: Function,
  onImportComplete?: () => void
};

// Table configuration for Tenants
const TENANTS_TABLE_CONFIG = {
  columns: [
    {
      key: 'checkbox',
      headerLabel: '',
      width: 44,
      flex: '0 0 auto',
      hideOnMobile: true,
      sortable: false
    },
    {
      key: 'name',
      headerLabel: 'Customer',
      width: 250,
      flex: '0 0 auto',
      sortable: true
    },
    {
      key: 'tags',
      headerLabel: 'Tag',
      width: 300,
      flex: '0 0 auto'
    },
    {
      key: 'phone',
      headerLabel: 'Phone',
      width: 175,
      flex: '0 0 auto',
      sortable: true
    },
    {
      key: 'email',
      headerLabel: 'Email',
      width: 150,
      flex: '0 0 auto',
      sortable: true
    },
    {
      key: 'dateAdded',
      headerLabel: 'Date Added',
      flex: '1 1 auto',
      minWidth: 150,
      sortable: true
    }
  ],
  actions: [
    {
      key: 'call',
      icon: PhoneIcon,
      label: 'Call Customer',
      onClick: 'handleCallTenant'
    },
    {
      key: 'message',
      icon: 'icon-sent',
      label: 'Send a message',
      onClick: 'handleSendMessage'
    },
    {
      key: 'edit',
      icon: EditIcon,
      label: 'Edit Customer',
      onClick: 'handleEditTenant'
    },
    {
      key: 'remove',
      icon: RemoveIcon,
      label: 'Remove Customer',
      onClick: 'handleRemoveTenant'
    }
  ],
  defaultSort: 'name',
  enableMultiSelect: true
};

const TenantsContent = (props: Props) => {
  const {
    tenants,
    removeTenant,
    setTenants,
    orderTenants,
    selectedRowsCount,
    isCustomerSelected,
    handleSelectAllClick,
    handleSelectCustomer,
    hasInitiallyLoaded,
    tenantsNextPage,
    totalTenantsCount,
    setCountOfTenants,
    onImportComplete
  } = props;
  
  const theme = useTheme();
  const classes = createClasses({...styles, ...MemberStructuralStyles}, theme);
  const dispatch = useDispatch();
  const i18n = useI18n();
  
  // Redux selectors replacing store usage
  const user = useSelector(selectUser);
  const tags = useSelector(selectTags);
  const accountSettings = useSelector(selectAccountSettings);
  const defaultUnit = useSelector(selectDefaultUnit);
  const tenantEnableUpload = useSelector(selectTenantEnableUpload);
  const tenantsPageInfo = useSelector(selectTenantsPageInfo);
  const accountUsage = useSelector(selectAccountUsage);
  const loadingAccount = useSelector(state => state.common?.loadingModules?.loadingAccount || false);
  const isAppLoading = useSelector(state => state.common?.isAppLoading || false);
  
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [openConfirmRemoveDialog, setOpenConfirmRemoveDialog] = useState(false);
  const [openPrivateMessageDialog, setOpenPrivateMessageDialog] = useState(false);
  const [openEditTenantDialog, setOpenEditTenantDialog] = useState(false);
  const [openCallDialog, setOpenCallDialog] = useState(false);
  const [sortTenantsByProp, setSortTenantsByProp] = useState('name');

  // Map column keys to backend fields for sorting
  const SORT_FIELD_MAP = {
    name: ['FirstName', 'LastName'],
    phone: ['Phone'],
    email: ['Email'],
    dateAdded: ['CreatedDt']
  };

  // Handle sorting
  const handleSort = (columnKey, direction) => {
    setSortTenantsByProp(columnKey);
    const backendFields = SORT_FIELD_MAP[columnKey];
    if (backendFields) {
      orderTenants(direction, backendFields);
    }
  };

  // Convert tenantsPageInfo to format expected by ConfigurableTable
  const convertedSortInfo = {};
  if (tenantsPageInfo && tenantsPageInfo.sortOrder !== undefined) {
    convertedSortInfo[sortTenantsByProp] = tenantsPageInfo.sortOrder;
  }

  // Action handlers
  const actionHandlers = {
    handleCallTenant: (tenant) => {
      setSelectedTenant(tenant);
      setOpenCallDialog(true);
    },
    handleSendMessage: (tenant) => {
      setSelectedTenant(tenant);
      setOpenPrivateMessageDialog(true);
    },
    handleEditTenant: (tenant) => {
      setSelectedTenant(tenant);
      setOpenEditTenantDialog(true);
    },
    handleRemoveTenant: (tenant) => {
      setSelectedTenant(tenant);
      setOpenConfirmRemoveDialog(true);
    }
  };

  // Cell renderer for each column
  const renderCell = (tenant, column, index) => {
    switch (column.key) {
      case 'checkbox':
        return (
          <Checkbox
            checked={isCustomerSelected(tenant._id)}
            color='primary'
            onChange={(e) => handleSelectCustomer(e, tenant._id)}
            size='medium'
          />
        );
      case 'name':
        return `${tenant.FirstName} ${tenant.LastName}`;
      case 'tags':
        return (
          <TagRenderer
            callbackOnTagPicked={callbackOnTagPicked(tenant._id)}
            callbackOnTagRemoved={callbackOnTagRemoved(tenant._id)}
            readonly={true}
            tagsIds={tenant.Tags}
          />
        );
      case 'phone':
        return formatPhoneNumberForFrontend(tenant.Phone);
      case 'email':
        return tenant.Email || '';
      case 'dateAdded':
        return moment().diff(tenant.CreatedDt, 'days') > 30
          ? moment(tenant.CreatedDt).format('L')
          : moment(tenant.CreatedDt).from(moment());
      default:
        return null;
    }
  };

  // Mobile item renderer
  const renderMobileItem = (tenant, index) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ minWidth: 0, width: 0, flex: 1, overflow: 'hidden' }}>
        <Typography variant="body1" sx={{ marginBottom: theme.spacing(0.5) }}>
          {`${tenant.FirstName} ${tenant.LastName}`}
        </Typography>
        <Typography 
          variant="body2" 
          color="textSecondary"
          sx={{
            fontSize: '0.8rem',
            marginBottom: theme.spacing(0.5)
          }}
        >
          {formatPhoneNumberForFrontend(tenant.Phone)}
        </Typography>
        <TagRenderer
          callbackOnTagPicked={callbackOnTagPicked(tenant._id)}
          callbackOnTagRemoved={callbackOnTagRemoved(tenant._id)}
          readonly={true}
          tagsIds={tenant.Tags}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: theme.spacing(0.25), flexShrink: 0 }}>
        <IconButton
          aria-label={`Call ${i18n.t('tenant')}`}
          sx={baseTableStyles.mobileActionButton(theme)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            actionHandlers.handleCallTenant(tenant);
          }}
        >
          <PhoneIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
        <IconButton
          aria-label={`Send a message`}
          sx={baseTableStyles.mobileActionButton(theme)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            actionHandlers.handleSendMessage(tenant);
          }}
        >
          <i className={'icon-sent'} />
        </IconButton>
        <IconButton
          aria-label={`Edit ${i18n.t('tenant')}`}
          sx={baseTableStyles.mobileActionButton(theme)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            actionHandlers.handleEditTenant(tenant);
          }}
        >
          <EditIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
        <IconButton
          aria-label={`Remove ${i18n.t('tenant')}`}
          sx={baseTableStyles.mobileActionButton(theme)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            actionHandlers.handleRemoveTenant(tenant);
          }}
        >
          <RemoveIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Box>
    </Box>
  );

  const handleRemoveTenant = () => {
    if (selectedTenant !== null) {
      removeTenant(selectedTenant);
      setSelectedTenant(null);
    } else {
      // Show snack message
      dispatch(setSnackMessage({
        message: 'An error occured. Please try again.',
        level: 'error',
        duration: 3000
      }));
    }
  };

  const handleSendPMToTenant = (tenantIds, messagePayload, callback, failureCallback) => {
    log('[tenantsContent][sendPMToTenant] tenantIds, message: ', tenantIds, messagePayload);
    const [tenantId] = tenantIds;
    log('[tenantsContent][sendPMToTenant] tenantId: ', tenantId);
    SPBackend.sendPMToTenant(tenantId, '', messagePayload, user)
      .then((response) => {
        const { data } = response;
        log('[tenantsContent][sendPMToTenant] tenantId, response: ', tenantId, response);
        dispatch(setSnackMessage({
          message: data.message,
          level: data.success,
          duration: 3000
        }));
        callback();
      })
      .catch(error => {
        log('[tenantsContent][sendPMToTenant] error: ', error);
        dispatch(setSnackMessage({
          message: 'An error occured. Please try again.',
          level: 'error',
          duration: 3000
        }));
        failureCallback();  
      });
  };

  const handleEditTenant = (
    tenant: Tenant,
    moveTasks: boolean,
    callback: Function = null
  ) => {
    if (selectedTenant !== null) {
      // Clone tenant to ensure all nested properties are mutable
      const mutableTenant = {
        ...tenant,
        Tags: tenant.Tags ? [...tenant.Tags] : [],
        unitsDocs: tenant.unitsDocs ? [...tenant.unitsDocs.map(unit => ({ ...unit }))] : []
      };
      setSelectedTenant(mutableTenant);
    } else {
      // Show snack message
      dispatch(setSnackMessage({
        message: 'An error occured. Please try again.',
        level: 'error',
        duration: 3000
      }));
    }

    const formattedNb = formatPhoneNumberForBackend(tenant.Phone);
    // Validate email if non empty
    if (tenant.Email !== '' && !validateEmail(tenant.Email)) {
      dispatch(setSnackMessage({
        message: 'Invalid email.',
        level: 'error',
        duration: 3000
      }));
      return;
    }
    if (
      modelUtils.getValAtPath(
        accountSettings,
        ['Tenants', 'ReceiveNotificationsBy'],
        NOTIFICATION_CHANNELS.SMS
      ) === NOTIFICATION_CHANNELS.EMAIL
    ) {
      if (tenant.Email === '') {
        dispatch(setSnackMessage({
          message: 'A valid email is required.',
          level: 'error',
          duration: 3000
        }));
        return;
      }
    } else {
      // if account settings isn't to send email, default to SMS
      if (!formattedNb) {
        dispatch(setSnackMessage({
          message: 'Invalid phone number.',
          level: 'error',
          duration: 3000
        }));
        return;
      }
    }

    const payload = {
      ...tenant,
      Phone: formattedNb,
      moveTasks,
      // Filter out null, undefined, and temporary tag IDs
      Tags: (tenant.Tags || []).filter(tagId =>
        tagId != null && !String(tagId).startsWith('temp_')
      )
    };

    dispatch(setLoadingModule('editTenant', true));
    
    SPBackend.editTenant(payload, user)
      .then(response => {
        log('[handleEditTenant] response: ', response);
        const updatedTenant = response.data?.tenant;
        log('[editTenant] res: ', updatedTenant);

        // Handle empty response gracefully
        if (!updatedTenant) {
          dispatch(setSnackMessage({
            message: 'Failed to update tenant. Please try again.',
            level: 'error',
            duration: 3000
          }));
          dispatch(setLoadingModule('editTenant', false));
          return;
        }

        if (callback) {
          callback();
        }
        dispatch(setSnackMessage({
          message: `${i18n.t('Tenant')} updated`,
          level: 'success',
          duration: 3000
        }));
        dispatch(setLoadingModule('editTenant', false));
        
        // Simple solution: Use map to create a new array with the updated tenant
        const updatedTenants = tenants.map(t => 
          t._id === updatedTenant._id 
            ? {
                ...updatedTenant,
                Tags: updatedTenant.Tags ? [...updatedTenant.Tags] : [],
                unitsDocs: updatedTenant.unitsDocs ? [...updatedTenant.unitsDocs.map(unit => ({ ...unit }))] : []
              }
            : t
        );
        dispatch(setTenants(updatedTenants));
        
        // Update selectedTenant if needed
        if (selectedTenant && selectedTenant._id === updatedTenant._id) {
          setSelectedTenant({
            ...updatedTenant,
            Tags: updatedTenant.Tags ? [...updatedTenant.Tags] : [],
            unitsDocs: updatedTenant.unitsDocs ? [...updatedTenant.unitsDocs.map(unit => ({ ...unit }))] : []
          });
        }
        
        setCountOfTenants(totalTenantsCount);
      })
      .catch(error => {
        log('[handleEditTenant] error', error);
        dispatch(setSnackMessage({
          message: error.message || 'An error occured. Please try again.',
          level: 'error',
          duration: 3000
        }));
        dispatch(setLoadingModule('editTenant', false));
      });
    setSelectedTenant(null);
  };

  const callbackOnTagPicked = (customerId: string) => (tag: Tag) => {
    try {
      console.log('[callbackOnTagPicked] tagging... tag, customerId: ', tag, customerId);
      dispatch(setLoadingModule('tagCustomer', true));
      
      SPBackend.tagCustomers(tag._id, [customerId], user)
        .then((response) => {
          console.log('[callbackOnTagPicked] response: ', response);
          const { message, success } = response.data;
          // update tenants state with new tag
          setTenants((prevTenants) => {
            log('[callbackOnTagPicked] prev tenants: ', prevTenants);
            const clone = R.clone(prevTenants);
            const tenantToUpdateIndex = R.findIndex(R.propEq('_id', customerId))(clone);
            log('[callbackOnTagPicked] tenantToUpdateIndex: ', tenantToUpdateIndex, clone[tenantToUpdateIndex]);
            console.log('[callbackOnTagPicked] tag._id: ', tag._id);
            if (tenantToUpdateIndex !== -1) {
              const union = R.union(clone[tenantToUpdateIndex].Tags, [tag._id]);
              log('[callbackOnTagPicked] union: ', union);
              // FIXME: for now we just replace. later we'll have to allow to add multiple tags here
              // combine lists of tags to ensure no duplicates
              clone[tenantToUpdateIndex].Tags = union;
            }
            return clone;
          });
          dispatch(setSnackMessage({
            message: message,
            level: success ? 'success' : 'error',
            duration: 5000
          })); 
          dispatch(setLoadingModule('tagCustomer', false));
        })
        .catch((error) => {
          log('[callbackOnTagPicked] error: ', error);
          dispatch(setSnackMessage({ 
            message: error.response ? error.response.data : 'An error occured. Please try again',
            level: 'error',
            duration: 5000
          })); 
        });
    } catch (e) {
      dispatch(setSnackMessage({ 
        message: 'Please select a tag',
        level: 'error',
        duration: 5000
      })); 
    }
  };

  const callbackOnTagRemoved = (customerId: string) => (tag: Tag) => () => {
    try {
      log('[callbackOnTagRemoved]... customerId, tag: ', customerId, tag);
      dispatch(setLoadingModule('tagCustomer', true));
      
      SPBackend.removeTagFromCustomers(tag._id, [customerId], user)
        .then((response) => {
          console.log('[callbackOnTagRemoved] response: ', response);
          const { message, success } = response.data;
          // update tenants state with new tag
          setTenants((prevTenants) => {
            log('[setTenant] prev tenants: ', prevTenants);
            const clone = R.clone(prevTenants);
            const tenantIndex = R.findIndex(R.propEq('_id', customerId))(clone);
            log('[setTenant] tenantIndex: ', tenantIndex, clone[tenantIndex]);
            if (tenantIndex !== -1) {
              const otherTags = (t => t !== tag._id);
              clone[tenantIndex].Tags = R.filter(otherTags, clone[tenantIndex].Tags);
            }
            return clone;
          });
          dispatch(setSnackMessage({ 
            message: message,
            level: success ? 'success' : 'error',
            duration: 5000
          })); 
          dispatch(setLoadingModule('tagCustomer', false));
        })
        .catch((error) => {
          log('[callbackOnTagRemoved] error: ', error);
          dispatch(setSnackMessage({ 
            message: error.response ? error.response.data : 'An error occured. Please try again',
            level: 'error',
            duration: 5000
          }));
          dispatch(setLoadingModule('tagCustomer', false));
        });
    } catch (e) {
      log('[callbackOnTagRemoved] e: ', e);
      dispatch(setSnackMessage({ 
        message: 'Connection error. Please try again.',
        level: 'error',
        duration: 5000
      }));
      dispatch(setLoadingModule('tagCustomer', false));
    }
  };

  return (
    <ContentContainer
      hasSubheader={true}
      sx={{
        ...classes.customerTableContent,
        ...classes.tableContentWrapper
      }}
    >
      {
        tenantEnableUpload || (hasInitiallyLoaded && (!tenants || tenants.length === 0))
        ? <TenantsDropzone onImportComplete={onImportComplete} />
        : null
      }
          
      <Box sx={classes.mainContentContainer}>
        {hasInitiallyLoaded && (
          <>
            <Box sx={classes.resultsCount}>
              {
                tenants && selectedRowsCount > 0
                ? <span>&nbsp;</span> // ${selectedRowsCount} ${i18n.t(`${selectedRowsCount > 1 ? 'tenants' : 'tenant'}`)} selected.`
                : tenants && tenants.length > 0
                  ? tenants.length < totalTenantsCount
                    ? `Showing ${tenants.length} of ${totalTenantsCount} ${i18n.t('tenants')}`
                    : `${totalTenantsCount} ${i18n.t(totalTenantsCount === 1 ? 'tenant' : 'tenants')}`
                  : `0 ${i18n.t('tenants')} found.`}
            </Box>
            <Box sx={classes.tableWrapper}>
              {
                !loadingAccount && tenants
                ? <ConfigurableTable
                    data={tenants || []}
                    config={TENANTS_TABLE_CONFIG}
                    onSort={handleSort}
                    sortInfo={convertedSortInfo}
                    renderCell={renderCell}
                    renderMobileItem={renderMobileItem}
                    actionHandlers={actionHandlers}
                    emptyMessage="No customers found"
                    selectedRowsCount={selectedRowsCount}
                    handleSelectAllClick={handleSelectAllClick}
                    isItemSelected={isCustomerSelected}
                    totalCount={totalTenantsCount}
                  />
                : null
              }
            </Box>
          </>
        )}
      </Box>
      <MessageTenantDialog
        open={openPrivateMessageDialog}
        setOpen={setOpenPrivateMessageDialog}
        tenant={selectedTenant}
        triggerAction={handleSendPMToTenant}
      />
      <RemoveTenantDialog
        actionConfirm={handleRemoveTenant}
        open={openConfirmRemoveDialog}
        setOpen={setOpenConfirmRemoveDialog}
        tenant={selectedTenant}
      />
      <EditTenantDialog
        currTenant={selectedTenant}
        open={openEditTenantDialog}
        setOpen={setOpenEditTenantDialog}
        triggerAction={handleEditTenant}
      />
      <VoiceCallDialog
        open={openCallDialog}
        setOpen={setOpenCallDialog}
        tenant={selectedTenant}
      />
      <Paginator 
        firstPage={tenants}
        nextPage={tenantsNextPage}
        pageInfo={tenantsPageInfo}
        totalCount={totalTenantsCount}
      />
    </ContentContainer>
  );
};

export default TenantsContent;
